/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 */

import React from 'react';
import '../../form-elements/formElement.css';
import API from "../../../../../utils/api";
import EventEmitter from "../../../../../eventEmmiters/EventEmitter";
import FormElementGenerator from "../../form-elements/formElementGenerator";
import Loader from "../../../loader/loader";
import {toInteger} from "../../../../../utils/formValueUtils";

/**
 * HOC for the partner form
 */
class InstitutionUrlForm extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            // main partner fields
            institutionUrlId: null,
            institutionUrl: '',
            // values for institution dropdown
            institutionId: '',
            institutionName: '',
            // values for institution dropdown
            partnerUrlTypeId: '',
            partnerUrlTypeName: '',
            // error message container
            errors: {},
            // user token
            token: '',
            isDisplayLoader: true,
            // form fields
            fields: [
                {
                    inputType: 'textUrl',
                    labelName: 'Url *',
                    inputName: 'institutionUrl',
                    placeholder: 'Please enter Institution Url here',
                    required: true,
                    disabled: false
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Institution *',
                    // initial attribute (the value name of the selected option in the dropdown)
                    inputName: 'institutionId',
                    optionValueName: 'institutionId',
                    optionLabelName: 'institutionName',
                    required: true,
                    path: '/institution-urls',
                    valueType: 'institutions'
                },
                {
                    inputType: 'dropdown',
                    labelName: 'Partner Url Type *',
                    // initial attribute (the value name of the selected option in the dropdown)
                    inputName: 'partnerUrlTypeId',
                    optionValueName: 'partnerUrlTypeId',
                    optionLabelName: 'partnerUrlTypeName',
                    required: true,
                    path: '/institution-urls',
                    valueType: 'partnerUrlTypes'
                },
            ]
        };

        // get the parameter from URL
        const institutionId = this.props.match.params.id;

        // set icons to the header
        if (institutionId === undefined) {
            EventEmitter.emit('setPageHeaderData', {
                page: 'institutionUrls',
                pageName: "InstitutionUrls",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/institutionUrls",
                optionalIcons: ['backIcon','clearFormIcon']
            });
        } else {
            EventEmitter.emit('setPageHeaderData', {
                page: 'institutionUrls',
                pageName: "InstitutionUrls",
                pageLink: process.env.REACT_APP_BASE_URL + "/#/home/institutionUrls",
                optionalIcons: ['backIcon','clearFormIcon','deleteIcon']
            });
        }

        EventEmitter.on('saveFormData', this.handleSubmit);
        EventEmitter.on('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.on('deleteFormData', this.deleteInstitutionUrl);
        EventEmitter.on('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.on('clearForm', this.clearForm);
    }

    componentDidMount = async () => {
        // get parameter from URL
        const institutionUrlId = this.props.match.params.id;

        // get token
        const user = JSON.parse(localStorage.getItem('tmcUser'));
        const token = 'Bearer ' + user.token;

        const oldState = Object.assign({}, this.state);

        // fill the form if not a create partner
        if (institutionUrlId !== undefined) {
            const url = '/institution-urls?search=%7BinstitutionUrlId:' + institutionUrlId + '%7D';

            await API.get(url, {headers: {Authorization: token}}).then(res => {

                const data = res.data[0];
                oldState.institutionUrlId = (data.institutionUrlId !== undefined) ? data.institutionUrlId : null;
                oldState.institutionUrl = (data.institutionUrl !== undefined) ? data.institutionUrl : '';
                oldState.institutionId = (data.institutionId !== undefined) ? data.institutionId : '';
                oldState.institutionName = (data.institutionName !== undefined) ? data.institutionName : '';
                oldState.partnerUrlTypeId = (data.partnerUrlTypeId !== undefined) ? data.partnerUrlTypeId : '';
                oldState.partnerUrlTypeName = (data.partnerUrlTypeName !== undefined) ? data.partnerUrlTypeName : '' ;
                oldState.token = token;
                oldState.isDisplayLoader = false;

                this.setState(oldState);
            }).catch(er => {
                oldState.isDisplayLoader = false;
                this.setState(oldState);
                this.displayErrorsForAPICalls(er);
            });
        } else {
            oldState.token = token;
            oldState.isDisplayLoader = false;
            this.setState(oldState);
        }
    };

    componentWillUnmount = () => {
        EventEmitter.off('setPageHeaderData', {});
        EventEmitter.off('saveFormData', this.handleSubmit);
        EventEmitter.off('displayDeleteFormDataWarning', this.displayDeleteWarningMessage);
        EventEmitter.off('deleteFormData', this.deleteInstitutionUrl);
        EventEmitter.off('navigateToPreviousPage', this.navigateToPreviousPage);
        EventEmitter.off('clearForm', this.clearForm);
    };

    /**
     * set the state
     * @param event
     */
    handleChange = event => {
        if (event.optionValueName !== undefined) {
            this.setState({
                [event.optionValueName]: event.value,
                [event.optionLabelName]: event.label
            }, () => {
                this.validate(event.optionValueName, event.value)
            });
        } else {
            const key = event.currentTarget.name;
            const val = event.currentTarget.value;

            this.setState({
                [key]: val
            }, () => {
                this.validate(key, val)
            });
        }
    };

    handleSubmit = () => {
        // data to be sent
        let data = null;
        // check its a update or create
        if (this.state.institutionUrlId !== null) {
            data = {
                institutionUrlId: this.state.institutionUrlId,
                institutionUrl: this.state.institutionUrl,
                institutionId: this.state.institutionId,
                partnerUrlTypeId: this.state.partnerUrlTypeId
            };
        } else {
            data = {
                institutionUrl: this.state.institutionUrl,
                institutionId: toInteger(this.state.institutionId),
                partnerUrlTypeId: toInteger(this.state.partnerUrlTypeId)
            };
        }

        // validate each form field
        for (let [key, value] of Object.entries(data)) {
            this.validate(key, value);
        }

        // check weather there are error messages
        if (Object.values(this.state.errors).find(o => o !== '') === undefined) {
            // check update or create
            if (this.state.institutionUrlId !== null) {
                this.updateInstitutionUrl(data);
            } else {
                this.createInstitutionUrl(data);
            }
        }
    };

    validate = (key, value) => {
        let error = {};

        switch (key) {
            case 'institutionUrl':
                if (value === '')
                    error[key] = "Institution URL is Required!";
                else if (value === ' ')
                    error[key] = "Institution URL can't be an empty space!";
                else if ((new TextEncoder().encode(value).length > 128))
                    error[key] = "Institution URL can't have more than 128 characters!";
                else if ((value.indexOf("https://") === 0) || (value.indexOf("http://") === 0) || (value.indexOf("/") === 0))
                    error[key] = "";
                else
                    error[key] = "Institution URL must begin with with http://, https:// or / ";
                break;
            case 'institutionId':
                if (value === '')
                    error[key] = "Please select an Institution!";
                else
                    error[key] = "";
                break;
            case 'partnerUrlTypeId':
                if (value === '')
                    error[key] = "Please select a Partner Url Type!";
                else
                    error[key] = "";
                break;
            default:
                break;
        }

        const newState = Object.assign({}, this.state);
        newState.errors = Object.assign(newState.errors, error);

        this.setState(newState);
    };

    createInstitutionUrl = async (data) => {

        await API.post('/institution-urls',
            {
                "institutionUrl": data.institutionUrl,
                "institutionId": data.institutionId,
                "partnerUrlTypeId": data.partnerUrlTypeId,
            }, {headers: {Authorization: this.state.token}}
        ).then(res => {
            this.setAlert("Success", "Success", "Successfully Created the Institution Url!");
            this.props.history.push('/home/institutionUrls');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    updateInstitutionUrl = async (data) => {

        await API.put('/institution-urls',
            {
                "institutionUrlId": data.institutionUrlId,
                "institutionUrl": data.institutionUrl,
                "institutionId": data.institutionId,
                "partnerUrlTypeId": data.partnerUrlTypeId,
            },{headers: {Authorization: this.state.token}}
        ).then(response => {
            this.setAlert("Success", "Success", "Successfully updated the Institution Url!");
            this.props.history.push('/home/institutionUrls');
        }).catch(er => {
            this.displayErrorsForAPICalls(er);
        });
    };

    displayDeleteWarningMessage = () => {
        if (this.state.partnerId !== null) {
            EventEmitter.emit('warningModelData', {
                type: 'institutionUrls',
                name: 'this Institution Url'
            });
            EventEmitter.emit('openWarningModel', true);
        } else {
            EventEmitter.emit('setAlert', {
                openAlert: true,
                type: "Error",
                isDisplayAlertIcon: true,
                alertMessage: "You are trying to delete before loading the Institution Url",
                alertTitle: "Error"
            });
        }

    };

    deleteInstitutionUrl = async (data) => {
        if (data && (this.props.match.path === "/home/institutionUrls/:id")) {
            await API.delete('/institution-urls?institutionUrlId=' + this.state.institutionUrlId, {headers: {Authorization: this.state.token}})
                .then(res => {
                    this.setAlert("Success", "Success", "Successfully Deleted the Institution Urls");
                    this.props.history.push('/home/institutionUrls');
                }).catch(er => {
                    this.displayErrorsForAPICalls(er);
                });
        }
    };

    displayErrorsForAPICalls = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            this.setAlert("Error", "Error", "The selected item not found!");
        } else if (error.message.includes('422')) {
            this.setAlert("Error", "Sorry", "Institution Url is already exist!");
        } else if (error.response === undefined || error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            this.props.history.push('/');
        } else {
            this.setAlert("Error", "Error", "Something went wrong");
        }
    };

    setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    clearForm = () => {
        const newState = Object.assign({}, this.state);

        newState.institutionUrl = "";
        newState.institutionId = "";
        newState.institutionName = "";
        newState.partnerUrlTypeId = "";
        newState.partnerUrlTypeName = "";
        newState.errors = {};

        this.setState(newState);
    };

    navigateToPreviousPage = () => {
        this.props.history.push('/home/institutionUrls');
    };

    render() {
        return(
            <div role="form" className="container">
                {this.state.isDisplayLoader && <Loader/>}
                <div className="main-btn-container">
                    <div className="buttons" tooltip="Save" onClick={this.handleSubmit}>
                        <i className="fa fa-save fa-lg icon-primary" />
                    </div>
                </div>
                <form>
                    <FormElementGenerator currentState={this.state} handleChange={this.handleChange} />
                </form>
            </div>
        )
    }
}

export default InstitutionUrlForm;
